import React from 'react';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  ICandidateFoundFieldValues,
  ICandidateFoundSubAssetValues,
  ICondition,
  ICredOneDetails,
  IEngineRequirement,
} from 'app/models/Placement/CredOneCompliance';
import { missingField } from 'app/constants';
import { CredOneRequirementStatus } from './enum';

/**
 * Method to return the status icon for the requirements and conditions
 * @param status - status of the requirement/condition
 * @param isAssetExists - is asset present for a condition
 * @param getIconType - return the icon type
 */
const getIconStatus = (status: boolean, isAssetExists: boolean, getIconType = false) => {
  if (!status && !isAssetExists) {
    return getIconType ? CredOneRequirementStatus.MISSING : <ErrorOutlineIcon style={{ color: 'orange' }} />;
  } else if (!status) {
    return getIconType ? CredOneRequirementStatus.FAILED : <HighlightOffIcon color={'error'} />;
  } else if (status) {
    return getIconType ? CredOneRequirementStatus.PASSED : <CheckCircleOutlineIcon style={{ color: '#5FB661' }} />;
  }
};

/**
 * Method to add a new property to set requirement status
 */
const setupRequirementStatus = (requirementItem: IEngineRequirement) => {
  let isOneAssetFalse = undefined;
  if (requirementItem.rules.length) {
    const conditionGroups = requirementItem.rules[0].conditionGroup.conditionGroups;
    isOneAssetFalse = conditionGroups.filter(conditionGroup =>
      conditionGroup.conditions.find(conditionItem => conditionItem.isAssetExists === false),
    );
  }
  const isAssetExists = !isOneAssetFalse.length ? true : false;
  return isAssetExists;
};

/**
 * Method to setup requirements layer from compliance data
 * @param complianceData
 */
const setupRequirementsData = (complianceData: ICredOneDetails) => {
  const requirementsLayer = complianceData.engineRequirementSetResults.map(item => item.requirements)?.flat(1);
  return requirementsLayer.map(item => {
    const isAssetExists = setupRequirementStatus(item);
    const requirementStatusType = getIconStatus(item.result, isAssetExists, true);
    return {
      ...item,
      isAssetExists: isAssetExists,
      requirementStatusType: requirementStatusType,
    };
  });
};

/**
 * Method to create sub asset data by condition item
 * @param conditionItem
 */
const createSubAssetData = (conditionItem: ICondition) => {
  const subAssetData = conditionItem.subAssetData;
  if (subAssetData?.subAssets?.length) {
    const subAssetRow = buildConditionRows(
      'Sub Assets',
      buildExpectedValues(subAssetData.subAssets.map(item => item.subAssetName)),
      foundValues(subAssetData.candidateFoundSubAssetValues),
      subAssetData.reason,
    );
    return subAssetRow;
  }
};

/**
 * Method to build condition row based on the following field
 * @param fieldName
 * @param expectedValue
 * @param foundValue
 * @param reason
 */
const buildConditionRows = (fieldName: string, expectedValue: string, foundValue: string, reason: string) => ({
  fieldName: fieldName,
  expectedValue: expectedValue,
  foundValue: foundValue,
  reason: reason,
});

/**
 * Method to concat names and return expected values as string
 * @param names
 */
const buildExpectedValues = (names: string[]) => {
  return names.join();
};

/**
 * Method to display found values from the array
 * @param array
 */
const foundValues = (array: ICandidateFoundSubAssetValues[]) => {
  if (array.length) {
    const concatValues = array.map(item => item.value);
    return concatValues.join();
  } else {
    return missingField;
  }
};

/**
 * Method to display found values from the fields array
 * @param foundFieldItem
 */
const foundFieldValues = (candidateFoundFieldValues: ICandidateFoundFieldValues[]) => {
  const fieldValues = candidateFoundFieldValues.map(foundFieldItem => foundFieldItem.value);
  // Will remove after api integration
  // const fieldValues = candidateFoundFieldValues.map(foundFieldItem => createValueField(foundFieldItem));
  return fieldValues.length ? fieldValues.join() : missingField;
};

/**
 * Method to create fields data by the condition item
 * @param conditionItem
 */
const createFieldsData = (conditionItem: ICondition) => {
  const fields = conditionItem.fields;
  let result = [];
  if (fields?.length) {
    result = fields.map(fieldItem => {
      const fieldRow = buildConditionRows(
        fieldItem.name,
        buildExpectedValues(fieldItem.values.map(item => item.value)),
        foundFieldValues(fieldItem.candidateFoundFieldValues),
        fieldItem.reason,
      );
      return fieldRow;
    });
  }
  return result;
};

/**
 * Method to create asset formula data
 */
const createAssetFormulaData = (conditionItem: ICondition) => {
  return conditionItem?.assetFormulas?.map(assetFormulaItem =>
    buildConditionRows(
      assetFormulaItem.name,
      assetFormulaItem?.expectedValue,
      assetFormulaItem?.foundValue ? assetFormulaItem.foundValue : missingField,
      assetFormulaItem.reason,
    ),
  );
};

const setBorderColor = (requirementResult: boolean, isAssetExists: boolean) => {
  const iconStatusType = getIconStatus(requirementResult, isAssetExists, true);
  switch (iconStatusType) {
    case CredOneRequirementStatus.MISSING:
      return '3px solid #F28D17';
    case CredOneRequirementStatus.FAILED:
      return '3px solid #CC0000';
    case CredOneRequirementStatus.PASSED:
      return '3px solid #5FB661';
  }
};

export {
  getIconStatus,
  setupRequirementStatus,
  setupRequirementsData,
  createSubAssetData,
  createFieldsData,
  setBorderColor,
  createAssetFormulaData,
};
